import React, { useContext } from 'react';
import { useHistory } from 'react-router';
import Loader from '@cpg-its/ads.common.loading-circle';
import { useDispatch, useSelector } from 'react-redux';
import { PageHeader } from '../../../../global/components/layout/page-header';
import { Person } from '../../../../global/models/person-model';
import { patchPersonInfo, patchPublishMailingAddress } from '../../../../global/ducks/person-prc-api';
import { resetPendingData } from './published-address-helper';
import { AddressDisplay } from './address-display';
import { FormButtons } from '../../../../global/components/form-element/form-buttons';
import { ApiStatuses, BaseApiState } from '../../../../global/models/api-model';
import { setCurrentPerson, setPatchPersonStatus } from './personal-info-slice';
import { apiErrorClear } from '../../../../global/ducks/base-api';
import { setDocumentUploadStatus, toggleDocumentUpload } from './document-upload-slice';
import { ContentSpacer } from '../../../../global/components/layout/generic-content-spacer';
import { DividerSection } from '../../../../global/components/layout/divider-section';
import { DemographicsContext } from '../../../../global/models/demographics-model';
import { TcsmEpiApiState, TcsmStates } from '../../../../root/tcsm-model';
import { DocumentUploadState } from './document-upload-models';
import { processRequestWithDocumentUpload } from './document-upload-request';
import { appendDocument } from '../../../../global/helpers/form-data-helper';
import { AnnualEnrollmentContext, AnnualEnrollmentKey } from '../../../annual-enrollment/models/annual-enrollment-models';
import { AccountInfoRoutes } from '../../constants/routes';
import { ProcessName, RegisterCompletedProcess } from '../../../../global/helpers/analytics-helper';

interface Props {
	basePath?: string;
}
export function PublishedAddresssUpdate(props: Props) {
	const app = useContext(DemographicsContext);
	const epiProps = useSelector<TcsmStates, TcsmEpiApiState>(state => state.epiProps);
	const apiState = useSelector<TcsmStates, BaseApiState>(state => state.apiState);
	const currentPerson = useSelector<TcsmStates, Person>(state => state.personalInfo[app].currentPerson);
	const dispatch = useDispatch();
	const pendingUpdate = useSelector<TcsmStates, any>(state => state.personalInfo[app].pendingUpdateObject);
	const addressToUpdate = useSelector<TcsmStates, any>(state => state.personalInfo[app].newMailingAddress);
	const docUploadState = useSelector<TcsmStates, DocumentUploadState>(state => state.documentUpload[app]);
	const isAnnualEnrollment = useContext(AnnualEnrollmentContext) === AnnualEnrollmentKey.annualEnrollment;
	const history = useHistory();


	if (apiState.pendingRequests?.length > 0) {
		return (<Loader />);
	}

	const onPublishSuccessHandler = (response) => {
		dispatch(setCurrentPerson(response, app));
		resetPendingData(dispatch, app);
		dispatch(apiErrorClear());
		dispatch(setPatchPersonStatus(ApiStatuses.Success, app));
		dispatch(setDocumentUploadStatus(false, app));
		dispatch(toggleDocumentUpload(false, app));
		RegisterCompletedProcess(ProcessName.PersonalInformation);
	};

	const onFailHandler = () => {
		resetPendingData(dispatch, app);
		dispatch(setPatchPersonStatus(ApiStatuses.Failure, app));
		dispatch(setDocumentUploadStatus(false, app));
	};

	const onSuccessHandler = (response, publishAddress: boolean) => {
		if (publishAddress) {
			dispatch(patchPublishMailingAddress(currentPerson.person_guid, onPublishSuccessHandler, onFailHandler));
			return;
		}

		onPublishSuccessHandler(response);
	};

	const onDocUploadSuccess = (toUpdate: any, publishAddress: boolean) => (data) => {
		const withDocuments = appendDocument(toUpdate, data?.map(a => a?.data?.doc_id));
		dispatch(patchPersonInfo(currentPerson.person_guid, withDocuments, false, (response) => onSuccessHandler(response, publishAddress)));

		if (isAnnualEnrollment) {
			history.push(`${props.basePath}${AccountInfoRoutes.RelationshipsSegment}`);
		}
	};

	const updateHandler = (publishAddress: boolean) => {

		if (docUploadState.stagedDocuments && docUploadState.stagedDocuments.length > 0 && docUploadState.showDocumentUpload) {
			processRequestWithDocumentUpload(docUploadState, currentPerson.person_guid, dispatch,
				onDocUploadSuccess(pendingUpdate, publishAddress));
		} else {
			dispatch(patchPersonInfo(currentPerson.person_guid, pendingUpdate, false, (response) => onSuccessHandler(response, publishAddress), onFailHandler));
		}
	};

	return (
		<>
			<ContentSpacer>
				<PageHeader headerText={epiProps?.personalInfoSettingState?.epiContent?.publishedAddressTitle} description={epiProps?.personalInfoSettingState?.epiContent?.publishedAddressDescription} />
			</ContentSpacer>

			<DividerSection removeDivider>
				<ContentSpacer>
					<p><strong>{epiProps?.labelSettingState?.epiContent.currentPublishedLabel}</strong></p>
					<AddressDisplay
						address1={currentPerson?.masked_published_address_1}
						address2={currentPerson?.masked_published_address_2}
						address3={currentPerson?.masked_published_address_3}
						city={currentPerson?.published_city}
						state={currentPerson?.published_state}
						postalCode={currentPerson?.published_postal_code}
						country={currentPerson?.published_country}
					/>
				</ContentSpacer>
				<p><strong>{epiProps?.labelSettingState?.epiContent.newMailingLabel}</strong></p>
				<AddressDisplay
					address1={addressToUpdate?.address1 === currentPerson.mailing_address_1 ? currentPerson.masked_mailing_address_1 : addressToUpdate?.address1}
					address2={addressToUpdate?.address2 === currentPerson.mailing_address_2 ? currentPerson.masked_mailing_address_2 : addressToUpdate?.address2}
					address3={addressToUpdate?.address3 === currentPerson.mailing_address_3 ? currentPerson.masked_mailing_address_3 : addressToUpdate?.address3}
					city={addressToUpdate?.city}
					state={addressToUpdate?.state}
					postalCode={addressToUpdate?.postalCode}
					country={addressToUpdate?.country}
				/>
			</DividerSection>
			<FormButtons
				disabled={apiState.pendingRequests?.length > 0}
				submitText={epiProps?.personalInfoSettingState?.epiContent?.updateButtonText}
				cancelText={epiProps?.personalInfoSettingState?.epiContent?.keepCurrentButtonText}
				onSubmit={() => updateHandler(true)}
				onCancel={() => updateHandler(false)}
			/>
		</>
	);
}

PublishedAddresssUpdate.defaultProps = {
	basePath: '',
};
